import { supabase, isDemoMode } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

type DbUser = Database['public']['Tables']['users']['Row'];
type DbOrganization = Database['public']['Tables']['organizations']['Row'];

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  avatar_url: string | null;
  organization_id: string;
  role: string;
  assigned_properties: string[] | null;
  department: string | null;
  status: string;
  notification_prefs: Record<string, boolean>;
  created_at: string;
  updated_at: string;
}

export interface SignUpData {
  email: string;
  password: string;
  full_name: string;
  organization_name: string;
  property_type?: string;
}

export interface SignInData {
  email: string;
  password: string;
}

/**
 * Sign up a new user and create their organization
 * Uses a database function for atomic creation
 */
export async function signUp(data: SignUpData) {
  console.log('[Auth] Starting signup for:', data.email);

  if (isDemoMode()) {
    throw new Error('Cannot sign up in demo mode. Please configure Supabase credentials.');
  }

  // 1. Create the auth user
  console.log('[Auth] Creating auth user...');
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: data.email,
    password: data.password,
    options: {
      data: {
        full_name: data.full_name,
      },
      emailRedirectTo: `${window.location.origin}/login`,
    },
  });

  if (authError) {
    console.error('[Auth] Auth signup error:', authError);
    throw authError;
  }

  if (!authData.user) {
    console.error('[Auth] No user returned from signup');
    throw new Error('Failed to create user');
  }

  console.log('[Auth] Auth user created:', authData.user.id);
  console.log('[Auth] Email confirmation required:', !authData.session);

  // If no session, email confirmation is required
  if (!authData.session) {
    console.log('[Auth] Email confirmation required - skipping profile creation until confirmed');
    return {
      user: authData.user,
      organization: null,
      requiresEmailConfirmation: true
    };
  }

  const userId = authData.user.id;

  // 2. Use the database function to atomically create org and profile
  console.log('[Auth] Creating org and profile via RPC...');
  const { data: result, error: signupError } = await supabase.rpc('handle_user_signup', {
    p_user_id: userId,
    p_email: data.email,
    p_full_name: data.full_name,
    p_organization_name: data.organization_name,
    p_property_type: data.property_type || 'residential',
  });

  if (signupError) {
    console.error('[Auth] RPC signup error:', signupError);
    throw new Error(signupError.message || 'Failed to create organization and profile');
  }

  console.log('[Auth] RPC result:', result);

  // Check if the function returned an error
  if (result && !result.success) {
    console.error('[Auth] RPC returned error:', result.error);
    throw new Error(result.error || 'Failed to complete signup');
  }

  // 3. Fetch the created organization
  const organization = await getOrganization(result.organization_id);
  console.log('[Auth] Signup complete, organization:', organization?.name);

  return { user: authData.user, organization };
}

/**
 * Sign in an existing user
 */
export async function signIn(data: SignInData) {
  console.log('[Auth] Starting signin for:', data.email);

  if (isDemoMode()) {
    throw new Error('Cannot sign in with Supabase in demo mode. Please configure Supabase credentials.');
  }

  const { data: authData, error } = await supabase.auth.signInWithPassword({
    email: data.email,
    password: data.password,
  });

  if (error) {
    console.error('[Auth] Signin error:', error);
    throw error;
  }

  if (!authData.user) {
    console.error('[Auth] No user returned from signin');
    throw new Error('Failed to sign in');
  }

  console.log('[Auth] Signin successful, user:', authData.user.id);

  // Fetch user profile and organization
  const profile = await getUserProfile(authData.user.id);
  const organization = profile ? await getOrganization(profile.organization_id) : null;

  // Update last login
  await supabase
    .from('users')
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', authData.user.id);

  return { user: authData.user, profile, organization };
}

/**
 * Sign out the current user
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get the current auth user
 */
export async function getAuthUser(): Promise<User | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Get the current session
 */
export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Get user profile from our users table
 */
export async function getUserProfile(userId: string): Promise<DbUser | null> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }

  return data;
}

/**
 * Get organization by ID
 */
export async function getOrganization(organizationId: string): Promise<DbOrganization | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', organizationId)
    .single();

  if (error) {
    console.error('Error fetching organization:', error);
    return null;
  }

  return data;
}

/**
 * Update user profile
 */
export async function updateUserProfile(userId: string, updates: Partial<DbUser>) {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update organization
 */
export async function updateOrganization(organizationId: string, updates: Partial<DbOrganization>) {
  const { data, error } = await supabase
    .from('organizations')
    .update(updates)
    .eq('id', organizationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  if (error) throw error;
}

/**
 * Update password (after reset)
 */
export async function updatePassword(newPassword: string) {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) throw error;
}

/**
 * Listen to auth state changes
 */
export function onAuthStateChange(callback: (event: string, session: unknown) => void) {
  return supabase.auth.onAuthStateChange(callback);
}
